import {
  PdfGen,
  PdfUnit,
  PdfSize,
  PdfPoint,
  PdfRect,
  PdfFont,
  PdfBrush,
  ETextAlign,
} from '../../pdf/pdf';
import { ILayoutLabelGen } from '../layoutLabel';
import { IItemCount } from '@educraft/base';
import { OptsLabel } from '../optsLabel';
import { GUtil } from '../../pdf/gutil';
import '../../pdf/draw/primitives';
import '../../pdf/draw/string';

/**
 * Behacka (Track Game Board)
 *
 * Layout: 4 pages, landscape orientation.
 * Pages 1-2: 10 stations (5×2 grid) with Roman numerals and questions
 * Pages 3-4: Answer strips (10×1 grid)
 *
 * Migrated from C# EduFrame.Single.Generators.Behacka
 */
export class Behacka implements ILayoutLabelGen, IItemCount {
  countDefault(): number {
    return 10;
  }
  countMin(): number {
    return 1;
  }
  countMax(): number {
    return 10;
  }

  public async generate(pdf: PdfGen, opts: OptsLabel): Promise<void> {
    // 4 landscape pages
    const page1 = pdf.addPage(true);
    const page2 = pdf.addPage(true);

    const margin = PdfUnit.FromMillimeters(5);
    const size1 = new PdfSize(PdfSize.A4H.width / 5, PdfSize.A4H.height / 2);
    const size2 = new PdfSize(PdfSize.A4H.width, PdfSize.A4H.height / 3);

    const count = Math.min(opts.list.length, 10);
    const items = opts.list.slice(0, count);

    // Cell dimensions for font calculation
    const innerW1 = size1.width - 2 * margin;
    const innerH1 = (size1.height - 2 * margin) / 2;
    const cellSize1 = new PdfSize(innerW1, innerH1);

    const stripR = new PdfRect(margin, margin, size2.width - 2 * margin, size2.height - 2 * margin);
    const cellSize2 = new PdfSize(stripR.width / 10, stripR.height / 2);

    // Collect texts for each font group
    const page1LeftTexts = items.map((item) => [item.left.getText()]);
    const page1RightTexts = items.map((item) => [item.right.getText()]);
    const page2LeftTexts = items.map((item) => [item.left.getText()]);

    // Compute shared fonts using adjustFontMulti
    const font1Left = page1.adjustFontMulti(page1LeftTexts, GUtil.baseFont, cellSize1);
    const font1Right = page1.adjustFontMulti(page1RightTexts, GUtil.baseFont, cellSize1);
    const font2Left = page2.adjustFontMulti(page2LeftTexts, GUtil.baseFont, cellSize2);

    // Page 1: Question cards (5×2 grid)
    for (let x = 0; x < 5; x++) {
      for (let y = 0; y < 2; y++) {
        const index = y * 5 + x;
        if (index >= count) continue;

        const rect = new PdfRect(x * size1.width, y * size1.height, size1.width, size1.height);
        const r = new PdfRect(
          rect.x + margin,
          rect.y + margin,
          rect.width - 2 * margin,
          rect.height - 2 * margin
        );
        const t1 = new PdfRect(r.x, r.y, r.width, r.height / 2);
        const t2 = new PdfRect(r.x, r.y + r.height / 2, r.width, r.height / 2);

        page1.drawRectangle(GUtil.basePen, null, t1);
        page1.drawLabel(items[index].left, { rect: t1, font: font1Left });
        page1.drawLabel(items[index].right, { rect: t2, font: font1Right });

        page1.drawRectangle(GUtil.cutPen, null, rect);
        page1.drawRectangle(GUtil.basePen, null, r);
      }
    }

    // Page 2: Answer strips (10×1 horizontal)
    for (let row = 0; row < 3; row++) {
      const yOffset = row * size2.height;

      for (let index = 0; index < 10 && index < count; index++) {
        if (index >= count) continue;

        const t1 = new PdfRect(
          stripR.x + index * cellSize2.width,
          yOffset + stripR.y,
          cellSize2.width,
          cellSize2.height
        );
        const t2 = new PdfRect(t1.x, t1.y + cellSize2.height, cellSize2.width, cellSize2.height);

        page2.drawLabel(items[index].left, { rect: t1, font: font2Left });
        page2.drawRectangle(GUtil.basePen, null, t1);
        page2.drawRectangle(GUtil.basePen, null, t2);
      }

      page2.drawRectangle(GUtil.cutPen, null, new PdfRect(0, yOffset, size2.width, size2.height));
    }
  }
}
